import { Toast } from 'primereact/toast';
import React, { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants, ImportTemplates } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './PerformanceAreaComponent.scss';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import DataManagementDataTable, { DATA_MANAGEMENT_PAGE_SIZE } from '../DataManagement/DataManagementDataTable';
import { createDataManagementQuery, useDataManagementPagination } from '../DataManagement/useDataManagementPagination';

interface PerformanceArea {
  performanceAreaId: number;
  performanceAreaTitle: string;
  performanceAreaDescription: string;
  providerType: string;
}

const renderPerformanceAreaActions = (area: PerformanceArea, onEdit: (area: PerformanceArea) => void, onDelete: (area: PerformanceArea) => void) => (
  <>
    <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} aria-label={`Edit ${area.performanceAreaTitle}`} onClick={() => onEdit(area)} />
    <Button icon="pi pi-trash" className="p-button-text p-button-danger" aria-label={`Delete ${area.performanceAreaTitle}`} onClick={() => onDelete(area)} />
  </>
);

interface PerformanceAreaComponentProps {
  providerTypes: { label: string; value: string }[];
}

const PerformanceAreaComponent: React.FC<PerformanceAreaComponentProps> = ({ providerTypes }) => {
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('performance-area-styles');
    return cleanup;
  }, []);

  const { uploadFile } = useDataManagementFileUpload();
  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.PERFORMANCE_AREAS);
      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.PERFORMANCE_AREAS,
      });
      setCurrentPage(1);
      await refresh();
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance areas uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.performanceAreas,
    onUpload: handleExcelUpload,
    title: 'Import Performance Areas from Excel',
    prominentDownloadLink: true,
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const query = createDataManagementQuery({ providerType: providerTypeFilter, search });
  const {
    items: areas,
    totalRecords,
    page: currentPage,
    setPage: setCurrentPage,
    loading,
    setLoading,
    error,
    refresh,
  } = useDataManagementPagination<PerformanceArea>(ApiEndpoints.GetPagedPerformanceAreas, query, { errorPrefix: 'Failed to fetch performance areas' });
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedArea, setSelectedArea] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<any>(null);

  const toast = useRef<Toast>(null);

  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes: Array.from(new Set(areas.map(a => a.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));
  let dialogHeader = 'Edit Performance Area';
  if (isAddMode) dialogHeader = 'Add Performance Area';
  else if (selectedArea?.performanceAreaTitle) dialogHeader = `Edit Performance Area - ${selectedArea.performanceAreaTitle}`;

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading performance areas" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div role="alert" style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Performance Area" icon="pi pi-plus" onClick={() => {
            setSelectedArea({ performanceAreaId: 0, performanceAreaTitle: '', performanceAreaDescription: '', providerType: '' });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="perf-area-search-anim">
          <input
            type="text"
            placeholder="Search by Title..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="perf-area-provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <DataManagementDataTable value={areas} dataKey="performanceAreaId" page={currentPage} totalRecords={totalRecords}
        onPage={setCurrentPage} loading={loading} emptyMessage="No performance areas found.">
        <Column header="#" headerStyle={{ width: 64, minWidth: 40 }} body={(_area: PerformanceArea, options) => (currentPage - 1) * DATA_MANAGEMENT_PAGE_SIZE + options.rowIndex + 1} />
        <Column field="performanceAreaTitle" header="Title" body={(area: PerformanceArea) => area.performanceAreaTitle || '-'} />
        <Column field="performanceAreaDescription" header="Description" bodyClassName="data-management-description-cell" body={(area: PerformanceArea) => area.performanceAreaDescription || '-'} />
        <Column field="providerType" header="Provider Type" body={(area: PerformanceArea) => area.providerType || '-'} />
        <Column header="Actions" body={(area: PerformanceArea) => renderPerformanceAreaActions(
          area,
          currentArea => { setSelectedArea({ ...currentArea }); setIsAddMode(false); setEditDialogVisible(true); },
          currentArea => { setAreaToDelete(currentArea); setDeleteDialogVisible(true); },
        )} />
      </DataManagementDataTable>

      {/* Add/Edit Dialog */}
      <Dialog
        header={dialogHeader}
        visible={editDialogVisible}
        style={{ width: '500px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.performanceAreas, selectedArea);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.performanceAreas, selectedArea);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddPerformanceArea, payload);
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance area added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                  // Refresh list after error
                  await refresh();
                } finally {
                  setLoading(false);
                }
              } else if (selectedArea) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdatePerformanceArea}/${selectedArea.performanceAreaId}`, { ...payload, isActive: selectedArea.isActive });
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Performance area updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Refresh list after error
                  await refresh();
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <PerformanceAreaEditForm editArea={selectedArea} setEditArea={setSelectedArea} providerTypesOptions={providerTypeOptions}/>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={async () => {
              if (!areaToDelete) return;
              try {
                setLoading(true);
                await httpService.delete(`${ApiEndpoints.DeletePerformanceArea}/${areaToDelete.performanceAreaId}`);
                await refresh();
                setDeleteDialogVisible(false);
                setAreaToDelete(null);
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Failed to delete performance area';
                toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
              } finally {
                setLoading(false);
              }
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{areaToDelete?.performanceAreaTitle ? `: ${areaToDelete.performanceAreaTitle}` : ''}?
        </div>
      </Dialog>
    </div>
  );
};

interface PerformanceAreaEditFormProps {
  editArea: any;
  setEditArea: (area: any) => void;
  providerTypesOptions:{
 label: string;
 value: string;
}[];
}

const PerformanceAreaEditForm: React.FC<PerformanceAreaEditFormProps> = ({ editArea, setEditArea,providerTypesOptions }) => {
  return (
    <DataManagementForm
      fields={dataManagementFormConfig.performanceAreas}
      values={editArea}
      onChange={setEditArea}
      options={{ providerType: providerTypesOptions }}
    />
  );
};

export default PerformanceAreaComponent;
